import { Component, Input, OnInit, ViewChild, EventEmitter, Output, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import * as _ from 'lodash';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import { VideoPlayerViewModel } from '../video-player/VideoPlayer.viewmodel';
import 'videojs-hotkeys'; // plugin
import 'videojs-hls-quality-selector'; // plugin
import '@videojs/http-streaming'; // plugin
import { FileHelper } from 'src/app/helpers';

/**
  * See: [Options Guide](https://docs.videojs.com/tutorial-options.html).
  */
type VideoJsOptions = {
  sources: {
      src?: string,
      type?: string,
  }[],
  tracks?: VideoJsTrack[],
  poster?: string,
  width?: number|string;
  height?: number|string;
  aspectRatio?: string,
  autoplay?: boolean,
  playbackRates?: number[];
  plugins?: any;
};

type VideoJsTrack = {
  kind: string;
  src: string;
  srclang?: LanguageCode;
  label?: Language;
  default?: boolean;
};

enum Language {
  en = 'English',
  es = 'Spanish',
  ku = 'Kurdish',
  vi = 'Vietnamese',
  ar = 'Arabic',
  zh = 'Chinese',
  de = 'German',
  fr = 'French',
  am = 'Amharic',
  ne = 'Nepali',
  hi = 'Hindi',
  ru = 'Russian',
}

type LanguageCode = keyof typeof Language;

/**
 * @example 30 seconds video:
 * <app-video-js-player #video src="http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4" width="900" height="620" autoplay></app-video-js-player>
 */
@Component({
  selector: 'app-video-js-player',
  templateUrl: './video-js-player.component.html',
  styleUrls: ['./video-js-player.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoJsPlayerComponent implements OnInit, OnDestroy, VideoPlayerViewModel {
  @ViewChild('target', { static: true })
  public target!: ElementRef;

  public player!: Player;

  /**
  * See: [Options Guide](https://docs.videojs.com/tutorial-options.html).
  */
  public options: VideoJsOptions = {
    sources: [{}],
    autoplay: false,
    // playbackRates: [0.5, 1, 1.5, 2],
    plugins: {
      hotkeys: {
        volumeStep: 0.1,
	  		seekStep: 5,
  			enableModifiersForNumbers: false,
        enableVolumeScroll: false,
        forwardKey: () => !this.preventFastForward,
      }
    }
  };

  @Input()
  public set src(url: string) {
    this.options.sources[0].src = url;
  }

  @Input()
  public set type(value: string) {
    this.options.sources[0].type = value;
  }

  @Input()
  public set poster(url: string) {
    this.options.poster = url;
  }

  @Input()
  public set captions(url: string) {
    if (url) {
      this.options.tracks = [{
        kind: 'captions',
        src: url,
        srclang: 'en',
        label: Language.en,
        default: false,
      }];
    }
    else {
      this.options.tracks = [];
    }
  }

  @Input()
  public set width(value: number|string) {
    this.options.width = value;
  }

  @Input()
  public set height(value: number|string) {
    this.options.height = value;
  }

  @Input()
  public set autoplay(value: boolean|string) {
    this.options.autoplay = value === true || value === 'true' || value === 'autoplay' || value === '';
  }

  @Input()
  public style = '';

  @Input()
  public preventFastForward = false;

  @Input()
  public useHlsStreaming = false;

  @Output()
  public loaded = new EventEmitter();

  @Output()
  public completed = new EventEmitter();

  public duration = 0; // sec
  public speed = 1; // normal
  public isLoaded = false;
  public isCompleted = false;

  ngOnInit(): void {
    // console.log('test videojs options', this.options);

    this.addTranslatedTracks();

    if (this.useHlsStreaming) { // Apple HLS for adaptive bitrate streaming with video quality selector
      this.switchToHlsStreaming();
    }

    this.player = videojs(this.target.nativeElement, this.options);

    this.rewindFastForward(this.player);

    this.player.on('playing', this.callback(() => {
      if (!this.isLoaded) {
        this.isLoaded = true;
        this.duration = this.player.duration() || 0;
        this.speed = this.player.playbackRate() || 1;

        this.loaded.emit();
      }
    }));

    this.player.on('ended', this.callback(() => {
      this.player
      if (!this.isCompleted) {
        this.isCompleted = true;
        this.completed.emit();
        this.player.exitFullscreen();
        this.player.hasStarted(false);
      }
    }));

    this.player.on('ratechange', this.callback(() => {
      this.speed = this.player.playbackRate() || 1;
    }));
  }

  ngOnDestroy(): void {
    if (this.player) {
      this.player.dispose();
    }
  }

  private callback<T extends Function>(fn: T) {
    return fn.bind(this) as T;
  }

  private addTranslatedTracks() {
    if (!this.options.tracks?.length) {
      return;
    }

    const tracks = this.options.tracks.slice();
    const englishTrack = tracks.find(track => track.srclang === 'en');

    if (englishTrack) {
      const translated = this.switchToTranslated(englishTrack);

      this.options.tracks = [englishTrack].concat(translated);
    }
  }

  private switchToTranslated(track: VideoJsTrack) {
    const exclude = track;

    const languages = _(Object.entries(Language))
      .filter(e => e[0] !== exclude.srclang)      // exclude default track in English
      .sortBy(e => e[1])                          // sort by language name
      .map(e => e[0]).value() as LanguageCode[];

    const tracks = languages.map((code): VideoJsTrack => {
      const language = Language[code];
      const segments = track.src.split('/');
      const filename = segments.pop() || '';
      const path     = segments.join('/');

      return {
        kind: 'captions',
        src: `${path}/translations/${filename.replace(/\.vtt$/i, `_${code}.vtt`)}`,
        srclang: code,
        label: language,
      };
    });

    return tracks;
  }

  private switchToHlsStreaming() {
    this.options.sources.forEach(source => {
      source.src = this.switchToHlsLink(source.src);
      source.type = 'application/x-mpegURL';
    });

    this.options.plugins.hlsQualitySelector = { displayCurrentQuality: true };
  }

  private switchToHlsLink(url: string|undefined) {
    if (!url) return url;
    if (url.endsWith('m3u8')) return url;
    const segments = url.split('/');
    const filename = segments.pop() || '';
    const path     = segments.join('/');
    const hlsPath  = `${path}/hls/${filename}/${FileHelper.changeFileExt(filename, 'm3u8')}`;
    return hlsPath;
  }

  private rewindFastForward(player: any, rewind = true) {
    let currentTime = 0;

    if (rewind) {
      player.on("seeking", () => {
        if (!this.preventFastForward) {
          return;
        }
        if (currentTime < player.currentTime()) {
          player.currentTime(currentTime);
        }
      });

      player.on("seeked", () => {
        if (!this.preventFastForward) {
          return;
        }
        if (currentTime < player.currentTime()) {
          player.currentTime(currentTime);
        }
      });

      player.on('timeupdate', () => {
        if (!this.preventFastForward) {
          return;
        }
        if (!player?.paused()) {
            currentTime = player.currentTime();
        }
      });
    } else {
      if (!this.preventFastForward) {
        return;
      }
      player.controlBar.progressControl.disable();
    }
  }
}
